import { DateTimeFormatter } from '@wix/bookings-date-time';
import { PriceUtils } from '@wix/bookings-uou-mappers';
import { Experiments } from '@wix/yoshi-flow-editor';
import {
  createBuyPlanAndBookOption,
  createBuyPlanBenefitOption,
  createCustomPriceOption,
  createDepositOption,
  createFreeOption,
  createFullAmountOption,
  createPayOfflineOption,
  createPayOnlineOption,
  createSubscriptionOption,
  createUsePlanOption,
  isDepositOrFullAmountFeatureEnabled,
  isOfferedAsOneTime,
} from '..';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  PaymentMethod,
  PaymentOption,
  TFunction,
} from '../../../../types/types';
import { isServiceOfferedAsPricingPlan } from '../../../mappers/serviceMapper/utils/payment.mapper';
import { isRuleBasedVariant } from '../../../mappers/dynamic-price.mapper';
import { SlotService } from '../../../state/types';

export const getSlotServicePaymentOptions = ({
  slotService,
  numberOfParticipants,
  isPricingPlanInstalled,
  t,
  dateAndTimeFormatter,
  dateRegionalSettingsLocale,
  experiments,
  isCart,
  excludePricingPlanOptions = false,
}: {
  slotService: SlotService;
  numberOfParticipants: number;
  isPricingPlanInstalled: boolean;
  t: TFunction;
  dateAndTimeFormatter: DateTimeFormatter;
  dateRegionalSettingsLocale: string;
  experiments: Experiments;
  isCart: boolean | undefined;
  excludePricingPlanOptions?: boolean;
}): PaymentOption[] => {
  const isSubscription =
    slotService.paymentDetails.subscriptionInfo &&
    experiments.enabled(ExperimentsConsts.SubscriptionPricingUoU);
  if (isSubscription) {
    const formattedPrice = PriceUtils.getFormattedCurrency({
      price: slotService.paymentDetails.price,
      currency: slotService.paymentDetails.currency,
      locale: dateRegionalSettingsLocale,
      currencyDisplay: 'narrowSymbol',
    });
    return [
      createSubscriptionOption(
        t,
        slotService.paymentDetails.subscriptionInfo!.totalPayments,
        formattedPrice,
      ),
    ];
  }

  const options = excludePricingPlanOptions
    ? []
    : slotService.memberships?.eligibleMemberships?.map((membership) =>
        createUsePlanOption(
          membership,
          numberOfParticipants,
          t,
          dateAndTimeFormatter,
          undefined,
        ),
      ) ?? [];

  // add invalid options if at least one option is eligible
  if (
    !excludePricingPlanOptions &&
    options.length > 0 &&
    slotService.memberships?.invalidMemberships?.length
  ) {
    options.push(
      ...slotService.memberships.invalidMemberships
        .filter((membership) => membership.membership?.credits?.remaining !== 0)
        .map((membership) =>
          createUsePlanOption(
            membership.membership!,
            numberOfParticipants,
            t,
            dateAndTimeFormatter,
            membership.reason,
          ),
        ),
    );
  }

  const showBuyAPricingPlan = isServiceOfferedAsPricingPlan(
    slotService.service.payment,
    isPricingPlanInstalled,
  );

  if (
    !excludePricingPlanOptions &&
    options.length === 0 &&
    showBuyAPricingPlan
  ) {
    const isPlanAndBookEnabled = experiments.enabled(
      ExperimentsConsts.PlanAndBook,
    );
    if (isPlanAndBookEnabled) {
      // Plan & Book: surface purchasable plans as "buy plan & book" options,
      // one per covering benefit, replacing the legacy "Buy a plan" entries.
      options.push(
        ...(slotService.purchasableBenefits?.map(
          ({ plan, benefit, redemptionCost }) =>
            createBuyPlanAndBookOption({
              plan,
              benefit,
              redemptionCost,
              numberOfParticipants,
              t,
            }),
        ) ?? []),
      );
    } else {
      options.push(
        ...(slotService.eligiblePlans?.map((plan) =>
          createBuyPlanBenefitOption(plan),
        ) ?? []),
      );
    }
  }
  const showPaySingleSession = isOfferedAsOneTime(slotService.service.payment);

  if (showPaySingleSession) {
    let price = slotService.paymentDetails.price;
    const selectedVariant = slotService.dynamicPriceInfo?.selectedVariants?.[0];
    if (selectedVariant && !isRuleBasedVariant(selectedVariant)) {
      const priceString =
        slotService.dynamicPriceInfo?.serviceOptionsAndVariants?.variants?.values?.find(
          (value) =>
            value.choices?.find(
              (choice) =>
                (choice.custom &&
                  choice.custom === selectedVariant?.choices?.[0].custom) ||
                (choice.staffMemberId &&
                  choice.staffMemberId ===
                    selectedVariant?.choices?.[0].staffMemberId) ||
                (choice.duration &&
                  choice.duration.minutes ===
                    selectedVariant?.choices?.[0].duration?.minutes),
            ),
        )?.price?.value!;

      price = Number(priceString);
    }

    // When an automatic discount applies, the Pay Now option shows the catalog
    // (pre-discount) price — the discount itself is applied at checkout. Since
    // `paymentDetails.price` already reflects the discounted amount, fall back to
    // `priceBeforeDiscount` for display whenever a discount rule is present.
    const { discountRuleName, priceBeforeDiscount } =
      slotService.paymentDetails;
    const displayPrice =
      discountRuleName && Number.isFinite(priceBeforeDiscount)
        ? priceBeforeDiscount!
        : price;

    const deposit = slotService.paymentDetails.minCharge;
    const isDepositeOrFullAmountEnabled = isDepositOrFullAmountFeatureEnabled({
      experiments,
      fullUpfrontPaymentAllowed:
        slotService.paymentDetails.fullUpfrontPaymentAllowed,
      deposit,
      paymentTypes: slotService.service.paymentTypes,
    });

    if (isDepositeOrFullAmountEnabled) {
      // Full amount shows the catalog (pre-discount) price in the suffix, like
      // the Pay Now option; the deposit shows the deposit amount itself.
      const formattedFullPrice = PriceUtils.getFormattedCurrency({
        price: displayPrice,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      const formattedDeposit = PriceUtils.getFormattedCurrency({
        price: deposit,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      options.push(
        createDepositOption(formattedDeposit, t, discountRuleName, isCart),
      );
      options.push(
        createFullAmountOption(formattedFullPrice, t, discountRuleName, isCart),
      );
    } else if (
      slotService.service.paymentTypes.includes(PaymentMethod.ONLINE)
    ) {
      const formattedPrice = PriceUtils.getFormattedCurrency({
        price: displayPrice,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      options.push(
        createPayOnlineOption(formattedPrice, t, discountRuleName, isCart),
      );
    }
    if (slotService.service.paymentTypes.includes(PaymentMethod.OFFLINE)) {
      const isFree = slotService.service.payment.paymentDetails.isFree;
      const customPrice = slotService.service.payment.paymentDetails.priceText;
      if (isFree) {
        if (customPrice) {
          options.push(createCustomPriceOption(customPrice));
        } else {
          options.push(createFreeOption());
        }
      } else {
        options.push(
          createPayOfflineOption(
            PriceUtils.getFormattedCurrency({
              price: displayPrice,
              currency: slotService.paymentDetails.currency,
              locale: dateRegionalSettingsLocale,
              currencyDisplay: 'narrowSymbol',
            }),
            t,
            discountRuleName,
            isCart,
          ),
        );
      }
    }
  }

  return options;
};
